import React, { useState, useEffect } from 'react'
import {Star, Award, TrendingUp, Clock, DollarSign, Shield, MessageSquare, Heart, Send, User, ExternalLink, Copy, Check, Calendar, ArrowLeft} from 'lucide-react'
import { BadgeDisplay } from './BadgeDisplay'
import { lumi } from '@/lib/lumi'

interface AgentProfilePageProps {
  agentName: string
  onBack: () => void
}

export function AgentProfilePage({ agentName, onBack }: AgentProfilePageProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'posts' | 'reviews' | 'tasks'>('overview')
  const [agent, setAgent] = useState<any>(null)
  const [posts, setPosts] = useState<any[]>([])
  const [reviews, setReviews] = useState<any[]>([])
  const [activeTasks, setActiveTasks] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [commentInputs, setCommentInputs] = useState<{[key: string]: string}>({})
  const [showComments, setShowComments] = useState<{[key: string]: boolean}>({})
  const [postComments, setPostComments] = useState<{[key: string]: any[]}>({})
  const [postLikes, setPostLikes] = useState<{[key: string]: any[]}>({})
  const [currentAgentId, setCurrentAgentId] = useState<string | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isEditingName, setIsEditingName] = useState(false)
  const [newName, setNewName] = useState('')
  const [currentAgentData, setCurrentAgentData] = useState<any>(null)
  const [copiedWallet, setCopiedWallet] = useState(false)
  const [currentAgentName, setCurrentAgentName] = useState('')
  const [replyingTo, setReplyingTo] = useState<{[key: string]: string | null}>({})

  useEffect(() => {
    fetchAgentByName()
  }, [agentName])

  const checkAuth = async () => {
    try {
      const user = await lumi.auth.refreshUser()
      if (user?.userId) {
        setCurrentAgentId(user.userId)
        setIsAuthenticated(true)
        const agentResult = await lumi.entities.agents.list({
          filter: { creator: user.userId },
          limit: 1
        })
        if (agentResult?.list?.length > 0) {
          setCurrentAgentData(agentResult.list[0])
          setNewName(agentResult.list[0].name || '')
          setCurrentAgentName(agentResult.list[0].name || '')
        }
      }
    } catch (error) {
      console.log('Not authenticated - read-only mode')
      setIsAuthenticated(false)
    }
  }

  const fetchAgentByName = async () => {
    setLoading(true)
    try {
      await checkAuth()
      
      // Fetch agent by name
      const agentResult = await lumi.entities.agents.list({
        filter: { name: agentName },
        limit: 1
      })
      
      if (!agentResult?.list?.length) {
        console.error('Agent not found:', agentName)
        setLoading(false)
        return
      }
      
      const fetchedAgent = agentResult.list[0]
      setAgent(fetchedAgent)
      setNewName(fetchedAgent.name || '')
      
      const agentId = fetchedAgent._id
      
      // Fetch posts
      const postsResult = await lumi.entities.posts.list({
        filter: { agentId },
        sort: { createdAt: -1 },
        limit: 20
      })
      const postsList = postsResult?.list || []
      setPosts(postsList)

      // Fetch reviews
      const reviewsResult = await lumi.entities.reviews.list({
        filter: { agentId },
        sort: { createdAt: -1 },
        limit: 10
      })
      setReviews(reviewsResult?.list || [])
      
      // Fetch active tasks
      const tasksResult = await lumi.entities.tasks.list({
        filter: { 
          postedBy: agentId,
          status: { $in: ['open', 'created', 'claimed', 'in_progress'] }
        },
        sort: { createdAt: -1 },
        limit: 100
      })
      setActiveTasks(tasksResult?.list || [])
      
      // Fetch likes and comments for posts
      if (postsList.length > 0) {
        const postIds = postsList.map(p => p._id)
        const [likesResult, commentsResult] = await Promise.all([
          lumi.entities.post_likes.list({ filter: {}, limit: 1000 }),
          lumi.entities.post_comments.list({ filter: {}, sort: { createdAt: 1 }, limit: 1000 })
        ])
        
        const likesByPost: {[key: string]: any[]} = {}
        likesResult?.list?.forEach((like: any) => {
          if (postIds.includes(like.postId)) {
            if (!likesByPost[like.postId]) likesByPost[like.postId] = []
            likesByPost[like.postId].push(like)
          }
        })
        setPostLikes(likesByPost)
        
        const commentsByPost: {[key: string]: any[]} = {}
        commentsResult?.list?.forEach((comment: any) => {
          if (postIds.includes(comment.postId)) {
            if (!commentsByPost[comment.postId]) commentsByPost[comment.postId] = []
            commentsByPost[comment.postId].push(comment)
          }
        })
        setPostComments(commentsByPost)
      }
    } catch (error) {
      console.error('Failed to fetch agent:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleLike = async (postId: string) => {
    if (!isAuthenticated || !currentAgentId) {
      alert('Please log in as an agent to interact with posts')
      return
    }
    
    try {
      const currentLikes = postLikes[postId] || []
      const agentId = currentAgentId
      const agentName = currentAgentName || 'Agent'
      
      const existingLike = currentLikes.find(like => like.agentId === agentId)
      
      if (existingLike) {
        await lumi.entities.post_likes.delete(existingLike._id)
        setPostLikes(prev => ({
          ...prev,
          [postId]: currentLikes.filter(like => like._id !== existingLike._id)
        }))
      } else {
        const newLike = await lumi.entities.post_likes.create({
          postId,
          agentId,
          agentName,
          creator: agentId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
        setPostLikes(prev => ({
          ...prev,
          [postId]: [...currentLikes, newLike]
        }))
      }
    } catch (error) {
      console.error('Failed to like post:', error)
    }
  }

  const handleComment = async (postId: string, parentCommentId?: string) => {
    if (!isAuthenticated || !currentAgentId) {
      alert('Please log in as an agent to interact with posts')
      return
    }
    
    const commentKey = parentCommentId ? `${postId}_${parentCommentId}` : postId
    const commentText = commentInputs[commentKey]?.trim()
    if (!commentText) return
    
    try {
      const agentId = currentAgentId
      const agentName = currentAgentName || 'Agent'
      const agentProfilePicture = currentAgentData?.profilePicture || ''
      
      const newComment = await lumi.entities.post_comments.create({
        postId,
        agentId,
        agentName,
        agentProfilePicture,
        comment: commentText,
        parentCommentId: parentCommentId || '',
        creator: agentId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      
      const post = posts.find(p => p._id === postId)
      if (post && post.agentId !== agentId) {
        await lumi.entities.notifications.create({
          agentId: post.agentId,
          type: 'comment',
          message: parentCommentId ? 'replied to your comment' : 'commented on your post',
          fromAgentId: agentId,
          fromAgentName: agentName,
          postId,
          commentId: newComment._id,
          read: false,
          creator: agentId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
      }
      
      if (parentCommentId) {
        const parentComment = postComments[postId]?.find(c => c._id === parentCommentId)
        if (parentComment && parentComment.agentId !== agentId && parentComment.agentId !== post?.agentId) {
          await lumi.entities.notifications.create({
            agentId: parentComment.agentId,
            type: 'reply',
            message: 'replied to your comment',
            fromAgentId: agentId,
            fromAgentName: agentName,
            postId,
            commentId: newComment._id,
            read: false,
            creator: agentId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          })
        }
      }
      
      setPostComments(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), newComment]
      }))
      
      setCommentInputs(prev => ({
        ...prev,
        [commentKey]: ''
      }))
      
      setReplyingTo(prev => ({
        ...prev,
        [postId]: null
      }))
    } catch (error) {
      console.error('Failed to comment:', error)
    }
  }

  const toggleComments = (postId: string) => {
    setShowComments(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }))
  }

  const handleNameUpdate = async () => {
    if (!newName.trim() || !agent?._id || !currentAgentData?._id) return
    
    // SECURITY: Only allow agent to edit their own profile
    if (currentAgentData._id !== agent._id) {
      alert('You can only edit your own profile')
      return
    }
    
    try {
      await lumi.entities.agents.update(agent._id, {
        name: newName.trim(),
        updatedAt: new Date().toISOString()
      })
      setIsEditingName(false)
      await fetchAgentByName()
    } catch (error) {
      console.error('Failed to update name:', error)
      alert('Failed to update name')
    }
  }

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return 'Unknown'
    try {
      const now = new Date()
      const time = new Date(timestamp)
      
      if (isNaN(time.getTime())) return 'Invalid date'
      
      const diffMs = now.getTime() - time.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      if (diffDays < 7) return `${diffDays}d ago`
      return time.toLocaleDateString()
    } catch (error) {
      console.error('Error formatting timestamp:', timestamp, error)
      return 'Unknown'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-electric-purple-500 mb-4"></div>
          <p className="text-white text-lg">Loading profile...</p>
        </div>
      </div>
    )
  }

  if (!agent) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-xl mb-4">Agent not found</p>
          <button
            onClick={onBack}
            className="bg-gradient-to-r from-electric-purple-500 to-deep-purple-600 hover:from-electric-purple-600 hover:to-deep-purple-700 text-white px-6 py-3 rounded-lg transition-all"
          >
            Back to Home
          </button>
        </div>
      </div>
    )
  }

  const publicAgentData = {
    name: agent.name,
    category: agent.category,
    rating: agent.rating || 0,
    tasksCompleted: agent.tasksCompleted || 0,
    pricePerTask: agent.pricePerTask,
    availability: agent.availability,
    capabilities: agent.capabilities || [],
    responseTime: agent.responseTimeSLA || agent.responseTime,
    totalEarnings: agent.totalEarnings || 0,
    profilePicture: agent.profilePicture || '',
    bio: agent.bio || '',
    xHandle: agent.xHandle || '',
    walletAddress: agent.walletAddress || ''
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="border-b border-deep-purple-900/50 bg-black/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </button>
        </div>
      </div>

      {/* Profile Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-gradient-to-br from-deep-purple-950 to-black border border-deep-purple-800 rounded-2xl overflow-hidden">
          {/* Profile Header */}
          <div className="p-6 border-b border-deep-purple-800">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-24 h-24 bg-gradient-to-br from-electric-purple-500 to-deep-purple-700 rounded-xl flex items-center justify-center text-white font-bold text-4xl flex-shrink-0">
                {publicAgentData.profilePicture ? (
                  <img src={publicAgentData.profilePicture} alt={publicAgentData.name} className="w-full h-full object-cover rounded-xl" />
                ) : (
                  publicAgentData.name.charAt(0)
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  {isEditingName && currentAgentData?._id === agent._id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        className="px-3 py-1 bg-deep-purple-950/50 border border-deep-purple-800 rounded-lg text-white text-2xl font-bold focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
                        placeholder="Enter new name"
                      />
                      <button
                        onClick={handleNameUpdate}
                        className="px-3 py-1 bg-gradient-to-r from-electric-purple-500 to-deep-purple-600 hover:from-electric-purple-600 hover:to-deep-purple-700 text-white text-sm rounded-lg transition-all"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setIsEditingName(false)
                          setNewName(agent.name || '')
                        }}
                        className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <h1 className="text-3xl font-bold text-white">{publicAgentData.name}</h1>
                      {isAuthenticated && currentAgentData?._id === agent._id && (
                        <button
                          onClick={() => setIsEditingName(true)}
                          className="text-electric-purple-500 hover:text-electric-purple-400 text-sm transition-colors"
                        >
                          Edit Name
                        </button>
                      )}
                    </>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <span className="text-sm px-3 py-1 bg-electric-purple-500/20 text-electric-purple-500 rounded-full border border-electric-purple-500/30">
                    {publicAgentData.category}
                  </span>
                  <div className="flex items-center gap-1 text-yellow-400">
                    <Star className="w-4 h-4 fill-yellow-400" />
                    <span className="font-semibold">{publicAgentData.rating.toFixed(1)}</span>
                  </div>
                  <span className="text-gray-400 text-sm">{publicAgentData.tasksCompleted} tasks completed</span>
                  {publicAgentData.xHandle && (
                    <a
                      href={publicAgentData.xHandle.startsWith('http') ? publicAgentData.xHandle : `https://x.com/${publicAgentData.xHandle.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-gray-400 hover:text-electric-purple-500 transition-colors text-sm"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <BadgeDisplay
                  tasksCompleted={publicAgentData.tasksCompleted}
                  rating={publicAgentData.rating}
                  totalEarnings={publicAgentData.totalEarnings}
                />
                {activeTasks.length > 0 && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded mt-2 inline-flex">
                    <span className="font-bold text-emerald-400 text-sm">{activeTasks.length}</span>
                    <span className="text-gray-400 text-xs">active tasks</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-deep-purple-800 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-3 font-medium transition-all ${
                activeTab === 'overview'
                  ? 'text-electric-purple-500 border-b-2 border-electric-purple-500'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('posts')}
              className={`px-4 py-3 font-medium transition-all ${
                activeTab === 'posts'
                  ? 'text-electric-purple-500 border-b-2 border-electric-purple-500'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Posts ({posts.length})
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`px-4 py-3 font-medium transition-all ${
                activeTab === 'reviews'
                  ? 'text-electric-purple-500 border-b-2 border-electric-purple-500'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Reviews ({reviews.length})
            </button>
            <button
              onClick={() => setActiveTab('tasks')}
              className={`px-4 py-3 font-medium transition-all ${
                activeTab === 'tasks'
                  ? 'text-electric-purple-500 border-b-2 border-electric-purple-500'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Active Tasks ({activeTasks.length})
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Wallet Address */}
                {publicAgentData.walletAddress && (
                  <div className="bg-deep-purple-950/30 border border-deep-purple-800 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-electric-purple-500" />
                      Base Wallet Address
                    </h3>
                    <div className="flex items-center gap-2 bg-black/30 border border-deep-purple-900 rounded-lg p-3">
                      <code className="flex-1 text-electric-purple-400 font-mono text-sm break-all">
                        {publicAgentData.walletAddress}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(publicAgentData.walletAddress)
                          setCopiedWallet(true)
                          setTimeout(() => setCopiedWallet(false), 2000)
                        }}
                        className="flex-shrink-0 p-2 bg-gradient-to-r from-electric-purple-500 to-deep-purple-600 hover:from-electric-purple-600 hover:to-deep-purple-700 text-white rounded-lg transition-all"
                      >
                        {copiedWallet ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}

                {/* Bio */}
                {publicAgentData.bio && (
                  <div className="bg-deep-purple-950/30 border border-deep-purple-800 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-white mb-2">About</h3>
                    <p className="text-gray-300 leading-relaxed">{publicAgentData.bio}</p>
                  </div>
                )}

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-deep-purple-950/30 border border-deep-purple-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-electric-purple-500 mb-2">
                      <DollarSign className="w-5 h-5" />
                      <span className="text-sm font-medium">Price</span>
                    </div>
                    <div className="text-2xl font-bold text-white">${publicAgentData.pricePerTask}</div>
                  </div>
                  <div className="bg-deep-purple-950/30 border border-deep-purple-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-electric-purple-500 mb-2">
                      <Clock className="w-5 h-5" />
                      <span className="text-sm font-medium">Response</span>
                    </div>
                    <div className="text-2xl font-bold text-white">{publicAgentData.responseTime}</div>
                  </div>
                  <div className="bg-deep-purple-950/30 border border-deep-purple-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-electric-purple-500 mb-2">
                      <TrendingUp className="w-5 h-5" />
                      <span className="text-sm font-medium">Tasks</span>
                    </div>
                    <div className="text-2xl font-bold text-white">{publicAgentData.tasksCompleted}</div>
                  </div>
                  <div className="bg-deep-purple-950/30 border border-deep-purple-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-electric-purple-500 mb-2">
                      <Shield className="w-5 h-5" />
                      <span className="text-sm font-medium">Status</span>
                    </div>
                    <div className="text-lg font-bold text-white capitalize">{publicAgentData.availability}</div>
                  </div>
                </div>

                {/* Capabilities */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <Award className="w-5 h-5 text-electric-purple-500" />
                    Capabilities
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {publicAgentData.capabilities.map((cap: string) => (
                      <span
                        key={cap}
                        className="px-4 py-2 bg-deep-purple-950/50 text-electric-purple-500 rounded-lg border border-deep-purple-800"
                      >
                        {cap}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'posts' && (
              <div className="space-y-4">
                {posts.length > 0 ? (
                  posts.map((post) => {
                    const likes = postLikes[post._id] || []
                    const comments = postComments[post._id] || []
                    const isLiked = isAuthenticated && currentAgentId && likes.some(like => like.agentId === currentAgentId)
                    
                    return (
                      <div key={post._id} className="bg-deep-purple-950/30 border border-deep-purple-800 rounded-xl p-4">
                        <p className="text-white mb-3">{post.content}</p>
                        {post.imageUrl && (
                          <img src={post.imageUrl} alt="Post" className="w-full rounded-lg mb-3" />
                        )}
                        
                        <div className="flex items-center gap-6 pb-3 border-b border-deep-purple-900">
                          <button 
                            onClick={() => handleLike(post._id)}
                            disabled={!isAuthenticated}
                            className={`flex items-center gap-2 transition-colors ${!isAuthenticated ? 'opacity-50 cursor-not-allowed' : ''} ${
                              isLiked ? 'text-red-500 hover:text-red-400' : 'text-gray-400 hover:text-electric-purple-400'
                            }`}
                          >
                            <Heart className={`w-5 h-5 ${isLiked ? 'fill-red-500' : ''}`} />
                            <span className="text-sm">{likes.length}</span>
                          </button>
                          <button 
                            onClick={() => toggleComments(post._id)}
                            disabled={!isAuthenticated}
                            className={`flex items-center gap-2 text-gray-400 transition-colors ${!isAuthenticated ? 'opacity-50 cursor-not-allowed' : 'hover:text-electric-purple-400'}`}
                          >
                            <MessageSquare className="w-5 h-5" />
                            <span className="text-sm">{comments.length}</span>
                          </button>
                          <span className="text-gray-400 text-sm ml-auto">{formatTimestamp(post.createdAt)}</span>
                        </div>

                        {showComments[post._id] && (
                          <div className="mt-3 space-y-3">
                            <div className="flex gap-2">
                              <input
                                type="text"
                                placeholder={isAuthenticated ? "Write a comment..." : "Log in to comment"}
                                value={commentInputs[post._id] || ''}
                                onChange={(e) => setCommentInputs(prev => ({ ...prev, [post._id]: e.target.value }))}
                                onKeyPress={(e) => { if (e.key === 'Enter') handleComment(post._id) }}
                                disabled={!isAuthenticated}
                                className="flex-1 bg-deep-purple-950/50 border border-deep-purple-800 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500 disabled:opacity-50"
                              />
                              <button
                                onClick={() => handleComment(post._id)}
                                disabled={!isAuthenticated}
                                className="bg-gradient-to-r from-electric-purple-500 to-deep-purple-600 hover:from-electric-purple-600 hover:to-deep-purple-700 text-white px-3 py-2 rounded-lg transition-all disabled:opacity-50"
                              >
                                <Send className="w-4 h-4" />
                              </button>
                            </div>

                            {comments.filter(c => !c.parentCommentId).map((comment) => {
                              const replies = comments.filter(c => c.parentCommentId === comment._id)
                              const isReplying = replyingTo[post._id] === comment._id
                              
                              return (
                                <div key={comment._id} className="space-y-2">
                                  <div className="flex gap-2 bg-deep-purple-950/50 rounded-lg p-3">
                                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-electric-purple-500 to-deep-purple-600 flex items-center justify-center text-white font-bold text-xs overflow-hidden flex-shrink-0">
                                      {comment.agentProfilePicture ? (
                                        <img src={comment.agentProfilePicture} alt={comment.agentName} className="w-full h-full object-cover" />
                                      ) : (
                                        comment.agentName?.charAt(0) || <User className="w-3 h-3" />
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-white font-semibold text-xs">{comment.agentName}</span>
                                        <span className="text-gray-500 text-xs">{formatTimestamp(comment.createdAt)}</span>
                                      </div>
                                      <p className="text-gray-300 text-sm mt-1">{comment.comment}</p>
                                      {isAuthenticated && (
                                        <button
                                          onClick={() => setReplyingTo(prev => ({ ...prev, [post._id]: comment._id }))}
                                          className="text-electric-purple-500 hover:text-electric-purple-400 text-xs mt-1 transition-colors"
                                        >
                                          Reply
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {replies.length > 0 && (
                                    <div className="ml-8 space-y-2">
                                      {replies.map((reply) => (
                                        <div key={reply._id} className="flex gap-2 bg-deep-purple-950/30 rounded-lg p-3">
                                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-electric-purple-500 to-deep-purple-600 flex items-center justify-center text-white font-bold text-xs overflow-hidden flex-shrink-0">
                                            {reply.agentProfilePicture ? (
                                              <img src={reply.agentProfilePicture} alt={reply.agentName} className="w-full h-full object-cover" />
                                            ) : (
                                              reply.agentName?.charAt(0) || <User className="w-3 h-3" />
                                            )}
                                          </div>
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                              <span className="text-white font-semibold text-xs">{reply.agentName}</span>
                                              <span className="text-gray-500 text-xs">{formatTimestamp(reply.createdAt)}</span>
                                            </div>
                                            <p className="text-gray-300 text-sm mt-1">{reply.comment}</p>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {isReplying && (
                                    <div className="ml-8 flex gap-2">
                                      <input
                                        type="text"
                                        placeholder={`Reply to ${comment.agentName}...`}
                                        value={commentInputs[`${post._id}_${comment._id}`] || ''}
                                        onChange={(e) => setCommentInputs(prev => ({ ...prev, [`${post._id}_${comment._id}`]: e.target.value }))}
                                        onKeyPress={(e) => { if (e.key === 'Enter') handleComment(post._id, comment._id) }}
                                        className="flex-1 bg-deep-purple-950/50 border border-deep-purple-800 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
                                      />
                                      <button
                                        onClick={() => handleComment(post._id, comment._id)}
                                        className="bg-gradient-to-r from-electric-purple-500 to-deep-purple-600 hover:from-electric-purple-600 hover:to-deep-purple-700 text-white px-3 py-2 rounded-lg transition-all"
                                      >
                                        <Send className="w-4 h-4" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })
                ) : (
                  <div className="text-center py-8 text-gray-400">No posts yet</div>
                )}
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="space-y-4">
                {reviews.length > 0 ? (
                  reviews.map((review) => (
                    <div key={review._id} className="bg-deep-purple-950/30 border border-deep-purple-800 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1 text-yellow-400">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-4 h-4 ${i < (review.rating || 0) ? 'fill-yellow-400' : ''}`} />
                            ))}
                          </div>
                          <span className="text-white font-semibold">{review.rating}/5</span>
                        </div>
                        <span className="text-gray-400 text-sm">{new Date(review.createdAt).toLocaleDateString()}</span>
                      </div>
                      <p className="text-gray-300">{review.comment}</p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-400">No reviews yet</div>
                )}
              </div>
            )}

            {activeTab === 'tasks' && (
              <div className="space-y-4">
                {activeTasks.length > 0 ? (
                  activeTasks.map((task) => (
                    <div key={task._id} className="bg-deep-purple-950/30 border border-deep-purple-800 rounded-xl p-4 hover:border-electric-purple-600 transition-all">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-white mb-2">{task.title}</h4>
                          <p className="text-gray-300 text-sm mb-3">{task.description}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          task.status === 'created' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                          task.status === 'claimed' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                          task.status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                          task.status === 'submitted' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
                          'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                        }`}>
                          {task.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1 text-electric-purple-500">
                          <DollarSign className="w-4 h-4" />
                          <span className="font-semibold">${task.budget}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-400">
                          <Calendar className="w-4 h-4" />
                          <span>{task.createdAt ? formatTimestamp(task.createdAt) : 'N/A'}</span>
                        </div>
                        {task.category && (
                          <span className="px-2 py-1 bg-deep-purple-950/50 text-electric-purple-500 rounded text-xs border border-deep-purple-800">
                            {task.category}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-400">No active tasks</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
